import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Tooltip from '@/components/UI/Tooltip';
import { _isBlank, _isNil, _notNil } from '@/littledash';
import cn from 'classnames';
import { useCallback } from 'react';
import type { FC } from 'react';
import type { DataTableCellIndexCoordinate } from '@/components/UI/DataTableV2/DataTable.model';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';
import styles from '../DataTableV2.module.scss';
import { PickerDisplayFormat, usePickerCellBehavior } from '../hooks/usePickerCellBehavior';
import { useAutoInsertOnActivate } from '../hooks/useAutoInsertOnActivate';
import { useDataTable } from '../DataTableProvider';
import { useCellCoordinate } from '../hooks/useCellCoordinate';

interface SampleCollectedAtCellProps {
  coordinate: DataTableCellIndexCoordinate;
  positionOffset: { x: number; y: number };
}

/**
 * Sentinel value sent to API to trigger server-side timestamp calculation.
 * The API intercepts this value and replaces it with the actual current timestamp.
 */
const TIMESTAMP_PLACEHOLDER = '-1';

export const SampleCollectedAtCell: FC<SampleCollectedAtCellProps> = ({ coordinate, positionOffset }) => {
  const { dataTableService } = useDataTable();
  const { idCoordinate } = useCellCoordinate(coordinate);

  const { cell, picker, mouseHandlers } = usePickerCellBehavior({
    coordinate,
    positionOffset,
    displayFormat: PickerDisplayFormat.DateTimeHoursMinutes,
  });

  // Auto-insert placeholder value when cell activated on empty cell
  // API will calculate actual timestamp server-side
  useAutoInsertOnActivate({
    coordinate,
    getValue: () => TIMESTAMP_PLACEHOLDER,
  });

  const handleCollectClick = useCallback(() => {
    if (dataTableService.readonly || _isNil(idCoordinate) || _notNil(cell.value)) {
      return; // Only collect on empty cells in non-readonly mode
    }

    dataTableService
      .updateCells([
        {
          column_id: idCoordinate.column,
          row_id: idCoordinate.row,
          value: TIMESTAMP_PLACEHOLDER,
        },
      ])
      .then(() => dataTableService.validate())
      .catch((error) => {
        ExceptionHandler.captureException(
          new InVivoError('Failed to collect timestamp', {
            cause: error,
            slug: 'datatable-collect-click',
            level: 'warning',
            context: {
              coordinate: `(${coordinate.column},${coordinate.row})`,
            },
          })
        );
      });
  }, [dataTableService, idCoordinate, cell.value, coordinate.column, coordinate.row]);

  const showLoading = cell.state === 'Loading' || cell.state === 'Saving';
  const isEmptyOrPlaceholder = _isBlank(cell.value) || cell.value === TIMESTAMP_PLACEHOLDER;

  return (
    <div
      className={cn(styles.CellContainer, { [styles.groupBorder]: cell.hasGroupBorder })}
      style={picker.positionStyle}
      onMouseDown={mouseHandlers.onMouseDown}
      onMouseEnter={mouseHandlers.onMouseEnter}
      onMouseUp={mouseHandlers.onMouseUp}
      onDoubleClick={picker.activate}
    >
      {showLoading ? (
        <div className={cn(styles.Cell, styles.Loading)} />
      ) : (
        <Tooltip render={cell.tooltip}>
          <div
            className={cn(
              styles.Cell,
              styles[cell.selectionState],
              { [styles.Active]: picker.active },
              styles[cell.state]
            )}
            data-test-component="SampleCollectedAtCell"
            data-test-element="collect-cell"
            data-test-key={`(${coordinate.column},${coordinate.row})`}
          >
            {isEmptyOrPlaceholder ? (
              <span className="link blue" onClick={handleCollectClick}>
                + Collect
              </span>
            ) : (
              <DateTimeRenderer value={cell.value ?? undefined} defaultResponse={cell.value ?? undefined} />
            )}
            <input
              className={styles.TimestampInput}
              style={{ visibility: picker.active ? 'visible' : 'hidden' }}
              ref={picker.inputRef}
              type="datetime-local"
              onChange={picker.handleInputChange}
            />
          </div>
        </Tooltip>
      )}
    </div>
  );
};
